import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Navbar, Footer } from '../components/Layout';
import { useTranslation } from '../context/TranslationContext';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Search, BookOpen, Filter, X, Layers, 
  Hammer, Paintbrush, Ruler, Wrench, ChevronLeft, ChevronRight,
  Star, SlidersHorizontal, ChevronDown, Sparkles
} from 'lucide-react';
import {
  categoryId,
  categoryLabel,
  safeArray,
  safeNum,
  coursesFromApiResponse,
} from '../lib/utils';
import SafeHTML from '../components/SafeHTML';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const categoryIcons = {
  materials: Layers,
  furniture: Hammer,
  finishing: Paintbrush,
  interior: Ruler,
  tools: Wrench,
  woodworking: Hammer,
  design: Paintbrush,
  techniques: Ruler,
};

const levelMeta = {
  beginner: { color: 'bg-green-500/10 text-green-600' },
  intermediate: { color: 'bg-amber-500/10 text-amber-600' },
  advanced: { color: 'bg-red-500/10 text-red-600' },
};

const CourseCatalog = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategories, setSelectedCategories] = useState(
    searchParams.get('category') ? searchParams.get('category').split(',') : []
  );
  const [selectedLevel, setSelectedLevel] = useState(searchParams.get('level') || '');
  const [minRating, setMinRating] = useState(searchParams.get('rating') || '');
  const [isFree, setIsFree] = useState(searchParams.get('free') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'order');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 12;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('page', currentPage.toString());
        params.append('limit', limit.toString());
        
        if (selectedCategories.length === 1) {
          params.append('category', selectedCategories[0]);
        }
        if (selectedLevel) params.append('level', selectedLevel);
        if (search) params.append('search', search);
        if (minRating) params.append('min_rating', minRating);
        if (isFree === 'true') params.append('is_free', 'true');
        if (isFree === 'false') params.append('is_free', 'false');
        if (sortBy && sortBy !== 'order') params.append('sort_by', sortBy);
        
        const [coursesRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/courses?${params}`),
          axios.get(`${API_URL}/categories`)
        ]);
        
        // Handle both array response and paginated response
        if (Array.isArray(coursesRes.data)) {
          let filteredCourses = coursesRes.data.filter(Boolean);
          // Client-side multi-category filter
          if (selectedCategories.length > 1) {
            filteredCourses = filteredCourses.filter(c =>
              selectedCategories.includes(categoryId(c.category))
            );
          }
          setCourses(filteredCourses);
          setTotal(filteredCourses.length);
          setTotalPages(Math.ceil(filteredCourses.length / limit));
        } else {
          let filteredCourses = coursesFromApiResponse(coursesRes.data).filter(Boolean);
          // Client-side multi-category filter
          if (selectedCategories.length > 1) {
            filteredCourses = filteredCourses.filter(c =>
              selectedCategories.includes(categoryId(c.category))
            );
          }
          setCourses(filteredCourses);
          setTotal(coursesRes.data.total || filteredCourses.length);
          setTotalPages(coursesRes.data.pages || 1);
        }
        setCategories(safeArray(categoriesRes.data));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategories, selectedLevel, search, minRating, isFree, sortBy, currentPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    updateSearchParams();
  };

  const updateSearchParams = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (selectedCategories.length > 0) params.set('category', selectedCategories.join(','));
    if (selectedLevel) params.set('level', selectedLevel);
    if (minRating) params.set('rating', minRating);
    if (isFree) params.set('free', isFree);
    if (sortBy !== 'order') params.set('sort', sortBy);
    setSearchParams(params);
  };

  const handleCategoryToggle = (categoryId) => {
    const newCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(c => c !== categoryId)
      : [...selectedCategories, categoryId];
    setSelectedCategories(newCategories);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategories([]);
    setSelectedLevel('');
    setMinRating('');
    setIsFree('');
    setSortBy('order');
    setCurrentPage(1);
    setSearchParams({});
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeFiltersCount = [
    selectedCategories.length > 0,
    selectedLevel,
    minRating,
    isFree
  ].filter(Boolean).length;

  const renderStars = (rating) => {
    const r = Math.round(safeNum(rating, 0));
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3.5 h-3.5 ${
              star <= r
                ? 'fill-amber-400 text-amber-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Interior Design & Carpentry Courses"
        description="Step-by-step video courses on interior execution, site measurement, cutting lists, material calculation, and technical drawing. Learn from professionals."
        keywords="interior technician training online, interior carpenter course, interior contractor course, interior site supervisor training, interior execution training, interior construction training, interior technical drawing course, how to prepare interior cutting list, how to measure kitchen cabinets, kitchen cabinet cutting list"
        url="/courses"
      />
      <Navbar />

      <main className="pt-20">
        {/* Header */}
        <section className="bg-secondary/30 py-16" data-testid="catalog-header">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h1 className="text-4xl md:text-5xl font-serif font-medium mb-4">
              {t('catalog_heading', 'Explore Courses')}
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl">
              {t(
                'catalog_subtitle',
                'Discover comprehensive courses on interior design, carpentry, and materials'
              )}
            </p>

            {/* Search */}
            <form onSubmit={handleSearch} className="mt-8 flex gap-3 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder={t('catalog_search_placeholder', 'Search courses...')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-12 h-12 rounded-full"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="rounded-full h-12 px-6" data-testid="search-btn">
                {t('catalog_search_btn', 'Search')}
              </Button>
            </form>
          </div>
        </section>

        {/* Filters & Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {/* Filter Bar */}
            <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="filter-bar">
              {/* Categories Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full gap-2">
                    <Layers className="w-4 h-4" />
                    {t('catalog_categories', 'Categories')}
                    {selectedCategories.length > 0 && (
                      <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                        {selectedCategories.length}
                      </Badge>
                    )}
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel>{t('catalog_select_categories', 'Select Categories')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {categories.map((cat) => {
                    const id = categoryId(cat);
                    const label = categoryLabel(cat);
                    const Icon = categoryIcons[id] || Layers;
                    return (
                      <DropdownMenuCheckboxItem
                        key={id || label}
                        checked={selectedCategories.includes(id)}
                        onCheckedChange={() => handleCategoryToggle(id)}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {label || id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Level Filter */}
              <Select value={selectedLevel} onValueChange={(value) => { setSelectedLevel(value === 'all' ? '' : value); setCurrentPage(1); }}>
                <SelectTrigger className="w-[140px] rounded-full h-9">
                  <SelectValue placeholder={t('catalog_level', 'Level')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('catalog_all_levels', 'All Levels')}</SelectItem>
                  <SelectItem value="beginner">{t('level_beginner', 'Beginner')}</SelectItem>
                  <SelectItem value="intermediate">{t('level_intermediate', 'Intermediate')}</SelectItem>
                  <SelectItem value="advanced">{t('level_advanced', 'Advanced')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Rating Filter */}
              <Select value={minRating} onValueChange={(value) => { setMinRating(value === 'all' ? '' : value); setCurrentPage(1); }}>
                <SelectTrigger className="w-[140px] rounded-full h-9">
                  <Star className="w-4 h-4 mr-2 text-amber-400" />
                  <SelectValue placeholder={t('catalog_rating', 'Rating')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('catalog_any_rating', 'Any Rating')}</SelectItem>
                  <SelectItem value="4">{t('catalog_stars_4', '4+ Stars')}</SelectItem>
                  <SelectItem value="4.5">{t('catalog_stars_45', '4.5+ Stars')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Price Filter */}
              <Select value={isFree} onValueChange={(value) => { setIsFree(value === 'all' ? '' : value); setCurrentPage(1); }}>
                <SelectTrigger className="w-[140px] rounded-full h-9">
                  <Sparkles className="w-4 h-4 mr-2" />
                  <SelectValue placeholder={t('catalog_price', 'Price')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('catalog_all_courses_filter', 'All Courses')}</SelectItem>
                  <SelectItem value="true">{t('catalog_free_only', 'Free Only')}</SelectItem>
                  <SelectItem value="false">{t('catalog_premium_only', 'Premium Only')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select value={sortBy} onValueChange={(value) => { setSortBy(value); setCurrentPage(1); }}>
                <SelectTrigger className="w-[150px] rounded-full h-9">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  <SelectValue placeholder={t('catalog_sort', 'Sort by')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="order">{t('catalog_sort_order', 'Course Order')}</SelectItem>
                  <SelectItem value="newest">{t('catalog_sort_newest', 'Newest')}</SelectItem>
                  <SelectItem value="rating">{t('catalog_sort_rating', 'Highest Rated')}</SelectItem>
                  <SelectItem value="popular">{t('catalog_sort_popular', 'Most Popular')}</SelectItem>
                  <SelectItem value="title">{t('catalog_sort_title', 'Alphabetical')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Clear Filters */}
              {activeFiltersCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters} 
                  data-testid="clear-filters"
                  className="text-destructive hover:text-destructive"
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('catalog_clear', 'Clear')} ({activeFiltersCount})
                </Button>
              )}
            </div>

            {/* Active Category Pills */}
            {selectedCategories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedCategories.map((catId) => {
                  const cat = categories.find((c) => categoryId(c) === catId);
                  const Icon = categoryIcons[catId] || Layers;
                  return (
                    <Badge 
                      key={catId}
                      variant="secondary"
                      className="px-3 py-1.5 gap-2 cursor-pointer hover:bg-secondary/80"
                      onClick={() => handleCategoryToggle(catId)}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {categoryLabel(cat) || catId}
                      <X className="w-3.5 h-3.5 ml-1" />
                    </Badge>
                  );
                })}
              </div>
            )}

            {/* Results count */}
            <p className="text-sm text-muted-foreground mb-6" data-testid="results-count">
              {t('catalog_showing', 'Showing')} {courses.length} {t('catalog_courses_word', 'courses')}
              {search && ` ${t('catalog_for', 'for')} "${search}"`}
              {total > limit && ` (${total} ${t('catalog_total', 'total')})`}
            </p>

            {/* Course grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="aspect-[4/3]" />
                    <CardContent className="p-6 space-y-3">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-6 w-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-24" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : courses.length === 0 ? (
              <div className="text-center py-20" data-testid="no-results">
                <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  {t('catalog_no_results_title', 'No courses found')}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {t('catalog_no_results_hint', 'Try adjusting your search or filters')}
                </p>
                <Button variant="outline" onClick={clearFilters}>
                  {t('catalog_clear_filters', 'Clear Filters')}
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="course-grid">
                  {courses.filter(Boolean).map((course) => (
                    <Link 
                      key={course.id || course._id} 
                      to={`/courses/${course.id}`}
                      className="group"
                      data-testid={`course-${course.id}`}
                    >
                      <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 h-full">
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img 
                            src={course.thumbnail_url || 'https://images.unsplash.com/photo-1713652492101-4705c38e9b58?crop=entropy&cs=srgb&fm=jpg&q=85'}
                            alt={course.title || 'Course'}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {course.is_free && (
                            <Badge className="absolute top-4 left-4 bg-green-600 text-white">
                              {t('label_free', 'Free')}
                            </Badge>
                          )}
                          {course.level && levelMeta[course.level] && (
                            <Badge className={`absolute top-4 right-4 ${levelMeta[course.level].color}`}>
                              {course.level === 'beginner'
                                ? t('level_beginner', 'Beginner')
                                : course.level === 'intermediate'
                                  ? t('level_intermediate', 'Intermediate')
                                  : t('level_advanced', 'Advanced')}
                            </Badge>
                          )}
                        </div>
                        <CardContent className="p-6">
                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            {safeNum(course.order, 0) > 0 && (
                              <Badge className="bg-accent text-accent-foreground" data-testid={`course-number-${course.id}`}>
                                {t('catalog_course_label', 'Course')} {course.order}
                              </Badge>
                            )}
                            <Badge variant="secondary" className="capitalize">{categoryLabel(course.category)}</Badge>
                          </div>
                          <h3 className="text-xl font-semibold mb-2 group-hover:text-accent transition-colors line-clamp-2">
                            {course.title}
                          </h3>
                          <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                            {course.description?.replace(/<[^>]*>/g, '') || ''}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <BookOpen className="w-4 h-4" />
                                {safeNum(course.lesson_count, 0)} {t('label_lessons', 'lessons')}
                              </span>
                            </div>
                            {safeNum(course.review_count, 0) > 0 ? (
                              <div className="flex items-center gap-1.5">
                                {renderStars(safeNum(course.average_rating, 0))}
                                <span className="text-sm text-muted-foreground">
                                  ({safeNum(course.review_count, 0)})
                                </span>
                              </div>
                            ) : (
                              <span className="font-semibold text-accent">
                                {course.is_free ? t('label_free', 'Free') : t('label_premium', 'Premium')}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12" data-testid="pagination">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      {t('catalog_prev', 'Previous')}
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let page;
                        if (totalPages <= 5) {
                          page = i + 1;
                        } else if (currentPage <= 3) {
                          page = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          page = totalPages - 4 + i;
                        } else {
                          page = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            className="w-10"
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      {t('catalog_next', 'Next')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CourseCatalog;
